import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/csr/MagnifyingGlass";
import { useId, useState } from "react";
import { Input } from "@volca/ui/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { Label } from "@volca/ui/components/ui/label";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type {
  PayrollRosterField,
  RosterBooleanFilter,
  RosterFieldFilter,
  RosterFieldValueSummary,
  RosterOptionsFilter,
  RosterRangeFilter,
  RosterTextFilter,
} from "@/payroll/roster/types";
import { humanizeRosterOption } from "@/payroll/roster/utils";
import {
  formatRosterValue,
  parseRosterRangeBound,
} from "@/payroll/roster/utils";
import {
  FilterCheckRow,
  OPTION_SEARCH_THRESHOLD,
  toggleValue,
} from "./filter-control-parts";

interface FilterBodyProps<TFilter extends RosterFieldFilter> {
  field: PayrollRosterField;
  options: string[];
  summary: RosterFieldValueSummary | undefined;
  filter: TFilter;
  onChange: (filter: RosterFieldFilter) => void;
}

export const OptionsFilterBody = ({
  field,
  options,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterOptionsFilter>) => {
  const idPrefix = useId();
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();
  const visibleOptions = options
    .map((option, index) => ({
      option,
      index,
      label: humanizeRosterOption(option),
    }))
    .filter(
      ({ option, label }) =>
        option.toLocaleLowerCase().includes(query) ||
        label.toLocaleLowerCase().includes(query),
    );

  if (options.length === 0) {
    return (
      <Text variant="caption" tone="muted" className="px-3 py-4">
        This field has no choices to filter by yet.
      </Text>
    );
  }

  return (
    <Stack className="w-full" align="start" gap={0}>
      {options.length >= OPTION_SEARCH_THRESHOLD && (
        <Stack
          align="start"
          gap={0}
          className="w-full border-b px-3 py-2 [&>div]:w-full"
        >
          <InputGroup>
            <InputGroupAddon align="inline-start">
              <MagnifyingGlassIcon />
            </InputGroupAddon>
            <InputGroupInput
              value={search}
              aria-label={`Search ${field.label} values`}
              placeholder="Search values"
              onChange={(event) => setSearch(event.target.value)}
            />
          </InputGroup>
        </Stack>
      )}
      <Stack
        align="start"
        gap={0}
        className="max-h-64 w-full overflow-y-auto p-1"
      >
        {visibleOptions.length === 0 ? (
          <Text variant="caption" tone="muted" className="py-4 text-center">
            No matching values
          </Text>
        ) : (
          visibleOptions.map(({ option, index, label }) => (
            <FilterCheckRow
              key={option}
              id={`${idPrefix}-${index}`}
              label={label}
              count={summary?.counts[option] ?? 0}
              checked={filter.values.includes(option)}
              onToggle={() =>
                onChange({
                  ...filter,
                  values: toggleValue(filter.values, option),
                })
              }
            />
          ))
        )}
      </Stack>
    </Stack>
  );
};

export const BooleanFilterBody = ({
  field,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterBooleanFilter>) => (
  <Stack align="start" gap={0} className="w-full p-1">
    {[true, false].map((value) => (
      <FilterCheckRow
        key={String(value)}
        id={`roster-filter-${field.key}-${value ? "yes" : "no"}`}
        label={value ? "Yes" : "No"}
        count={summary?.counts[String(value)] ?? 0}
        checked={filter.values.includes(value)}
        onToggle={() =>
          onChange({ ...filter, values: toggleValue(filter.values, value) })
        }
      />
    ))}
  </Stack>
);

export const RangeFilterBody = ({
  field,
  summary,
  filter,
  onChange,
}: FilterBodyProps<RosterRangeFilter>) => {
  const min = parseRosterRangeBound(filter.min);
  const max = parseRosterRangeBound(filter.max);
  const inverted = min !== null && max !== null && min > max;
  const inputs = [
    {
      id: `roster-filter-${field.key}-min`,
      label: "From",
      value: filter.min,
      key: "min",
      placeholder: summary?.min,
    },
    {
      id: `roster-filter-${field.key}-max`,
      label: "To",
      value: filter.max,
      key: "max",
      placeholder: summary?.max,
    },
  ] as const;

  return (
    <Stack align="start" gap={2} className="w-full p-3">
      <Stack className="w-full" align="start" direction="row" gap={2}>
        {inputs.map(({ id, label, value, key, placeholder }) => (
          <Stack
            align="start"
            key={key}
            gap={1}
            className="min-w-0 flex-1 [&>div]:w-full"
          >
            <Label htmlFor={id} className="text-xs text-subtle">
              {label}
            </Label>
            <Input
              id={id}
              inputMode="decimal"
              placeholder={placeholder == null ? "Any" : String(placeholder)}
              value={value}
              onChange={(event) =>
                onChange({ ...filter, [key]: event.target.value })
              }
            />
          </Stack>
        ))}
      </Stack>
      {inverted ? (
        <Text variant="caption" className="text-danger">
          “From” is higher than “To”, so nobody can match.
        </Text>
      ) : (
        summary?.min != null &&
        summary.max != null && (
          <Text variant="caption" tone="muted">
            {summary.min === summary.max
              ? `Everyone is at ${formatRosterValue(String(summary.min), field)}`
              : `Roster runs ${formatRosterValue(String(summary.min), field)} to ${formatRosterValue(String(summary.max), field)}`}
          </Text>
        )
      )}
    </Stack>
  );
};

export const TextFilterBody = ({
  field,
  filter,
  onChange,
}: FilterBodyProps<RosterTextFilter>) => (
  <Stack align="start" gap={0} className="w-full p-3 [&>div]:w-full">
    <Input
      autoFocus
      aria-label={`${field.label} contains`}
      placeholder="Contains…"
      value={filter.query}
      onChange={(event) => onChange({ ...filter, query: event.target.value })}
    />
  </Stack>
);
